import { useCallback, useEffect, useRef, useState } from "react";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import usePointerSort from "../../hooks/usePointerSort";

export function useSectionOrdering() {
  // Track which sections are collapsed (by section name). Persisted to
  // localStorage so the user's preference survives across modal opens.
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >(() => {
    try {
      const raw = localStorage.getItem(
        LocalStorageKeyEnum.CMDK_COLLAPSED_SECTIONS
      );
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });
  // Persist from an effect, not from inside the updater. A state updater runs
  // during the render phase and must be pure: writing localStorage there fires
  // twice under StrictMode, and persists a value for a render React is free to
  // discard.
  useEffect(() => {
    try {
      localStorage.setItem(
        LocalStorageKeyEnum.CMDK_COLLAPSED_SECTIONS,
        JSON.stringify(collapsedSections)
      );
    } catch {
      // ignore
    }
  }, [collapsedSections]);

  const toggleSectionCollapsed = useCallback((name: string) => {
    setCollapsedSections((prev) => ({ ...prev, [name]: !prev[name] }));
  }, []);

  // User-defined order of section names. Sections that don't appear in this
  // list fall back to their natural order (perpCategoryFilters order).
  const [sectionOrder, setSectionOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(LocalStorageKeyEnum.CMDK_SECTION_ORDER);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const persistSectionOrder = useCallback((order: string[]) => {
    try {
      localStorage.setItem(
        LocalStorageKeyEnum.CMDK_SECTION_ORDER,
        JSON.stringify(order)
      );
    } catch {
      // ignore
    }
  }, []);
  // Holds the current rendered section names so the pointer-sort hook can
  // map from/to indices back to names without needing them as deps.
  const renderedSectionNamesRef = useRef<string[]>([]);
  const onReorderSections = useCallback(
    (fromIdx: number, toIdx: number) => {
      const names = renderedSectionNamesRef.current;
      const fromName = names[fromIdx];
      const toName = names[toIdx];
      if (!fromName || !toName || fromName === toName) return;
      const order = [...names];
      const [moved] = order.splice(fromIdx, 1);
      order.splice(toIdx, 0, moved);
      setSectionOrder(order);
      persistSectionOrder(order);
    },
    [persistSectionOrder]
  );
  const sectionSort = usePointerSort({ onReorder: onReorderSections });

  return {
    collapsedSections,
    toggleSectionCollapsed,
    sectionOrder,
    renderedSectionNamesRef,
    sectionSort,
  };
}
